import { showNotice } from "../lib/confirmDialog";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useState, useEffect, useRef, type FormEvent, type KeyboardEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import BirthYearFeedback from "../components/BirthYearFeedback";
import { bankAccountOptionLabel } from "./BankAccountsSection";
import FieldHint from "../components/FieldHint";
import { parseAdditionalCategoryFee } from "../lib/registrationCategories";
import WizUpload from "../components/WizUpload";
import TournamentDivisionManager from "../components/TournamentDivisionManager";
import TournamentSetupProgress from "../components/TournamentSetupProgress";
import {
  api,
  ApiError,
  isSupportUser,
  SUPPORT_ROLE_LABEL,
  type PaymentBankAccount,
  type SportKey,
  type TournamentCopyNotice,
  type TournamentDivision,
  type TournamentDivisionInput,
} from "../lib/api";
import { SPORT_LABELS } from "../lib/labels";
import { uploadAssetHref } from "../lib/assets";
import { drawCeremonyPlanFromPolicy } from "../lib/drawCeremonyPlan";
import { uploadedFileName } from "../lib/ruleAttachments";
import {
  DIVISION_MODALITY_OPTIONS,
  applyDivisionModality,
  divisionBirthYearLabel,
  divisionDetailsLabel,
  divisionInputFromDraft,
  divisionModality,
  emptyDivisionDraft,
  type DivisionModality,
  type TournamentDivisionDraft,
  yearFromTournamentDate,
} from "../lib/tournamentDivisions";
import { emptyVenueRow, prepareAdditionalVenues, type VenueRowDraft } from "../lib/tournamentVenues";
import {
  MAX_SPONSORS,
  emptySponsorRow,
  splitSponsorBanners,
  sponsorBannersPayload,
  sponsorRowsError,
  type SponsorRowDraft,
} from "../lib/tournamentExtras";
import { tournamentStartNotice } from "../lib/registrationDates";
import { copyNoticeFromPolicy } from "../lib/tournamentCopy";
import { REGISTRATION_LINK_PREFIX, registrationLinkCollisionSuffix } from "../lib/registrationLink";

function registrationAmountForSettings(value: string) {
  const normalized = value.replace(/[^\d.,]/g, "").replace(/,/g, "");
  const amount = Number(normalized);
  return Number.isFinite(amount) ? String(amount) : "0";
}

/** «Club Norte · BBVA ****4821 · Club Norte AC»: CLABE enmascarada (la
 * completa solo va en la vista previa del jugador). */
function bankAccountSummary(account: PaymentBankAccount): string {
  return `${bankAccountOptionLabel(account)} · ${account.beneficiary_name}`;
}

function apiErrorDetail(err: unknown): string {
  return err instanceof ApiError && err.message ? err.message : "error de red";
}

function isValidOptionalUrl(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return true;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

interface AdminTournamentEditPageProps {
  /** "create": UN SOLO Paso 1 (Eduardo 2026-09-08 — "no quiero pasos 1
   * diferentes"): el alta usa exactamente esta pantalla; los archivos se
   * guardan en memoria y se suben en cuanto existe el id del torneo. */
  mode?: "create" | "edit";
}

type ImageSlot = "logo" | "organizer-logo" | "sponsor-banner" | "player-banner";
type ImageOpKind = "upload" | "confirm" | "remove";
type ImageSlotTournament = {
  logo_url: string | null;
  organizer_logo_url: string | null;
  sponsor_banner_url?: string | null;
  player_banner_url?: string | null;
};
const IMAGE_SLOTS: ImageSlot[] = ["logo", "organizer-logo", "sponsor-banner", "player-banner"];
const REMOVE_SURFACE: Record<ImageSlot, string> = {
  logo: "el póster",
  "organizer-logo": "el logo",
  "sponsor-banner": "el banner del link público",
  "player-banner": "el banner de la vista del jugador",
};

/** Archivo ya cargado (Eduardo 2026-09-10): el input nativo dice "Sin
 * archivos seleccionados" aunque ya haya póster/logo/banner; aquí se ve
 * la miniatura, el nombre y un link para abrirlo. */

function renderCurrentUpload(url: string | null, label: string) {
  if (!url) return null;
  const href = uploadAssetHref(url) ?? url;
  const name = uploadedFileName(url) ?? "ver archivo";
  return (
    <span className="upload-current">
      <img className="upload-current__thumb" src={href} alt="" />
      <span className="upload-current__copy">
        <span className="upload-current__label">{label}</span>
        <a className="upload-current__name" href={href} target="_blank" rel="noreferrer" title={name}>{name}</a>
      </span>
    </span>
  );
}

/** Anclas a las que se llega desde la Convocatoria. */
const JUMP_TARGETS = new Set(["#categorias", "#inscripcion-y-cobro"]);

export default function AdminTournamentEditPage({ mode = "edit" }: AdminTournamentEditPageProps = {}) {
  const isCreate = mode === "create";
  const { token, hasRole, isImpersonating, user } = useAuth();
  // Soporte Torneos123 (Eduardo 2026-10-29): edita el torneo, pero costos,
  // forma y cuenta de cobro quedan bloqueados (el servidor también los niega).
  const isSupport = isSupportUser(user);
  // Candado de comprobantes en el ALTA (Eduardo 2026-10-01, torneo 246): el
  // servidor rechaza «Transferencia / comprobante» con costo a quien no es
  // superadmin (403 manual_payments_locked) también al crear. Ofrecerla a un
  // organizador tumbaba el alta y se perdían sus categorías.
  const createManualAllowed = isCreate && hasRole("superadmin") && !isImpersonating;
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const tournamentId = isCreate ? NaN : id ? Number(id) : NaN;
  const { askConfirm } = useAbortableConfirm([tournamentId]);

  const [sport, setSport] = useState<SportKey>("tenis");
  const [name, setName] = useState("");
  // Link de inscripción (/inscripcion/<slug>): sale del nombre con el que se
  // creó y no cambia al renombrar (un link ya compartido sigue funcionando).
  const [publicSlug, setPublicSlug] = useState<string | null>(null);
  const [venueName, setVenueName] = useState("");
  const [googleMapsLink, setGoogleMapsLink] = useState("");
  // Solo lectura: los grupos de WhatsApp se editan en Comunicación (Eduardo
  // 2026-10-12). Datos ya NO manda whatsapp_group_link: el PUT los deja igual.
  const [whatsappGroupLink, setWhatsappGroupLink] = useState("");
  const [startDate, setStartDate] = useState("");
  // Aviso anti-dedazo junto al campo (mismo criterio que Inscripciones).
  const startDateNotice = tournamentStartNotice(
    startDate,
    (() => {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    })(),
    "datos",
  );
  const [endDate, setEndDate] = useState("");
  const [registrationDeadline, setRegistrationDeadline] = useState("");
  // Sorteo del cuadro (Eduardo 2026-09-11): se decide aquí, en Datos.
  const [drawCeremonyEnabled, setDrawCeremonyEnabled] = useState(false);
  const [drawCeremonyAt, setDrawCeremonyAt] = useState("");
  const [registrationCost, setRegistrationCost] = useState("");
  const [paymentProvider, setPaymentProvider] = useState(
    isCreate && !createManualAllowed ? "mercadopago_marketplace" : "manual",
  );
  // Alta: solo el superadmin (sin «Ver como admin») puede elegir cobro con
  // comprobantes; al organizador se le ofrece Mercado Pago, que es lo único
  // que el servidor le acepta. Al editar manda el permiso del torneo.
  const [manualPaymentsAllowed, setManualPaymentsAllowed] = useState(createManualAllowed);
  const [registrationPlatformFee, setRegistrationPlatformFee] = useState("0");
  // Vacío = no se ofrece ese precio (el form público ni pregunta).
  const [memberFee, setMemberFee] = useState("");
  const [coachFee, setCoachFee] = useState("");
  const [additionalCategoryFee, setAdditionalCategoryFee] = useState("");
  const additionalCategoryFeeCheck = parseAdditionalCategoryFee(additionalCategoryFee);
  const additionalCategoryFeeError = additionalCategoryFeeCheck.ok ? null : additionalCategoryFeeCheck.error;
  // Etiquetas de los costos diferenciados (vacío = "Socio" / "Profesores").
  const [memberFeeLabel, setMemberFeeLabel] = useState("");
  const [coachFeeLabel, setCoachFeeLabel] = useState("");
  const [bankAccounts, setBankAccounts] = useState<PaymentBankAccount[]>([]);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState("");
  // Cuenta de cobro que el torneo YA tiene (puede ser del organizador y no
  // estar en «mis cuentas») y si quien abre la pantalla puede cambiarla.
  // Otro admin completo (p. ej. tras duplicar un torneo del organizador)
  // guarda con la del organizador: no se le ofrecen las suyas.
  const [currentCollectionAccount, setCurrentCollectionAccount] = useState<PaymentBankAccount | null>(null);
  const [canChangeCollectionAccount, setCanChangeCollectionAccount] = useState(true);
  const [loadedBankAccountId, setLoadedBankAccountId] = useState("");
  // Pagos que ya tiene el torneo: avisar que el cambio aplica a pagos nuevos.
  const [collectionPayments, setCollectionPayments] = useState<{ paid: number; in_process: number } | null>(null);
  const [loadedPayoutLabel, setLoadedPayoutLabel] = useState<string | null>(null);
  const [officialBall, setOfficialBall] = useState("");
  const [organizerName, setOrganizerName] = useState("");
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const [organizerLogoUrl, setOrganizerLogoUrl] = useState<string | null>(null);
  const [sponsorBannerUrl, setSponsorBannerUrl] = useState<string | null>(null);
  const [playerBannerUrl, setPlayerBannerUrl] = useState<string | null>(null);
  // Subir/quitar imágenes (PR #590, revisión de Torni): UNA operación a la
  // vez por imagen. `imageBusy` pinta botones; el ref es el candado real
  // (dos clics antes del re-render). Cada operación lleva un número por
  // imagen: solo la vigente aplica su respuesta, así una respuesta vieja
  // (o de otro torneo) nunca pisa lo que ya se ve.
  const [imageBusy, setImageBusy] = useState<Partial<Record<ImageSlot, ImageOpKind>>>({});
  // Se aborta al cambiar de torneo o salir: cierra el «¿Quitar…?» que siga
  // abierto (el diálogo es global y sobrevive al desmontaje).
  const imageConfirmAbortRef = useRef<AbortController>(new AbortController());
  const imageBusyRef = useRef<Set<ImageSlot>>(new Set());
  const imageSeqRef = useRef<Record<ImageSlot, number>>({ logo: 0, "organizer-logo": 0, "sponsor-banner": 0, "player-banner": 0 });
  // Aviso de «Quitar» dentro de la tarjeta Imágenes (no al pie del formulario).
  const [imageError, setImageError] = useState<string | null>(null);
  const uploadingPoster = imageBusy.logo === "upload";
  const uploadingOrganizerLogo = imageBusy["organizer-logo"] === "upload";
  // Alta: los archivos se guardan aquí y se suben después de crear el torneo.
  const [pendingPoster, setPendingPoster] = useState<File | null>(null);
  const [pendingOrganizerLogo, setPendingOrganizerLogo] = useState<File | null>(null);
  const [pendingSponsorBanner, setPendingSponsorBanner] = useState<File | null>(null);
  const [pendingPlayerBanner, setPendingPlayerBanner] = useState<File | null>(null);
  // QA Cowork #11: por default UNA sola imagen alimenta ambas superficies
  // (link público y vistas del jugador — ambas la muestran completa y
  // escalada, sin recorte). El modo separado queda como avanzado y se
  // preserva para torneos que ya subieron imágenes distintas.
  const [separateBanners, setSeparateBanners] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  // Copia de otro torneo en borrador: aviso fijo arriba y póster resaltado.
  const [copyNotice, setCopyNotice] = useState<TournamentCopyNotice | null>(null);
  // Para que el aviso solo diga lo que sigue pendiente: PDF propio de la
  // convocatoria ya subido (paso 3) y ranking ya ligado (paso 7).
  const [callPdfUrl, setCallPdfUrl] = useState<string | null>(null);
  const [rankingLinkedNow, setRankingLinkedNow] = useState(false);
  const [isFederationOfficial, setIsFederationOfficial] = useState(false);
  const [divisions, setDivisions] = useState<TournamentDivision[]>([]);
  // Alta: categorías en borrador (se crean junto con el torneo).
  const [divisionForm, setDivisionForm] = useState<TournamentDivisionDraft>(emptyDivisionDraft);
  const [divisionDrafts, setDivisionDrafts] = useState<TournamentDivisionInput[]>([]);
  const [additionalVenues, setAdditionalVenues] = useState<VenueRowDraft[]>([]);
  // Varios patrocinadores y grupos de WhatsApp (Eduardo 2026-10-01): el 1 de
  // cada uno sigue en sus campos de siempre; estas filas son los extra.
  const [sponsorFirstName, setSponsorFirstName] = useState("");
  const [sponsorFirstLink, setSponsorFirstLink] = useState("");
  const [extraSponsors, setExtraSponsors] = useState<SponsorRowDraft[]>([]);
  const [uploadingExtraSponsor, setUploadingExtraSponsor] = useState<number | null>(null);
  // Sin banner principal (o sin link en «Link Grupo WhatsApp») el primer
  // extra ES el 1: así lo listan el link público y las vistas del jugador.
  const hasFirstSponsor = Boolean(sponsorBannerUrl || playerBannerUrl || pendingSponsorBanner || pendingPlayerBanner);
  const extraSponsorStart = hasFirstSponsor ? 2 : 1;
  const [loading, setLoading] = useState(!isCreate);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Categoría escrita sin agregar o edición sin guardar en el gestor (edición).
  const [pendingCategoryWarning, setPendingCategoryWarning] = useState<string | null>(null);
  // Aviso en «Categorías del torneo»: el sistema prendió los datos del papá,
  // mamá o tutor por la primera categoría de menores (Eduardo 2026-10-03).
  const [guardianFieldsAutoEnabled, setGuardianFieldsAutoEnabled] = useState(false);
  // QA Cowork #8: las credenciales emitidas congelan logo/empresa/nombre —
  // si la identidad cambia en esta sesión, se ofrece regenerarlas aquí mismo.
  const initialIdentityRef = useRef<{ name: string; organizerName: string; organizerLogoUrl: string | null } | null>(null);
  const [regeneratingCredentials, setRegeneratingCredentials] = useState(false);
  const [credentialRegenMessage, setCredentialRegenMessage] = useState<string | null>(null);
  // «Este torneo usa credencial» (Eduardo 2026-10-05): apagado, no se ofrece
  // regenerar credenciales.
  const [credentialEnabled, setCredentialEnabled] = useState(true);
  const tournamentYear = yearFromTournamentDate(startDate);

  const identityChanged =
    initialIdentityRef.current !== null &&
    (name.trim() !== initialIdentityRef.current.name ||
      organizerName.trim() !== initialIdentityRef.current.organizerName ||
      organizerLogoUrl !== initialIdentityRef.current.organizerLogoUrl);

  async function handleRegenerateCredentials() {
    if (!token || Number.isNaN(tournamentId) || regeneratingCredentials) return;
    if (
      !(await askConfirm(
        "Se rehacen TODAS las credenciales ya emitidas con el logo, empresa y nombre actuales (el encuadre de foto de cada una se conserva). ¿Continuar?", { acceptLabel: "Sí, regenerar credenciales" }))
    ) {
      return;
    }
    setRegeneratingCredentials(true);
    setCredentialRegenMessage(null);
    try {
      const response = await api.registrations.regenerateCredentials(token, tournamentId);
      const { regenerated, skipped_no_photo: skipped } = response.data;
      setCredentialRegenMessage(
        regenerated === 0 && skipped === 0
          ? "No hay credenciales emitidas todavía: las nuevas ya saldrán con la identidad actual."
          : `${regenerated} credencial(es) regeneradas con la identidad nueva${skipped > 0 ? `; ${skipped} sin foto se quedaron con la anterior` : ""}.`,
      );
    } catch (err) {
      setCredentialRegenMessage(
        err instanceof ApiError ? err.message : "No se pudieron regenerar las credenciales.",
      );
    } finally {
      setRegeneratingCredentials(false);
    }
  }

  useEffect(() => {
    if (!token) return;
    if (isCreate) {
      api.payments.account(token)
        .then((response) => {
          const accounts = response.data.bank_accounts ?? [];
          setBankAccounts(accounts);
          // Torneo nuevo: se propone la cuenta predeterminada.
          const preferred = accounts.find((account) => account.is_default);
          if (preferred) setSelectedBankAccountId((current) => current || String(preferred.id));
        })
        .catch(() => setBankAccounts([]));
      return;
    }
    if (Number.isNaN(tournamentId)) return;
    setLoading(true);
    Promise.all([
      api.tournaments.get(token, tournamentId),
      api.tournaments.listDivisions(token, tournamentId),
      api.registrations.get(token, tournamentId),
      api.payments.account(token),
    ])
      .then(([res, divisionsRes, registrationRes, paymentsRes]) => {
        const t = res.data.tournament;
        const registrationSettings = registrationRes.data.settings;
        setDivisions(divisionsRes.data.divisions);
        setName(t.name || "");
        setPublicSlug(t.slug || null);
        setVenueName(t.venue_name || "");
        setGoogleMapsLink(t.google_maps_link || "");
        setAdditionalVenues(
          (t.venues ?? [])
            .filter((venue) => !venue.is_primary)
            .map((venue) => ({
              name: venue.name,
              google_maps_link: venue.google_maps_link ?? "",
              court_count: venue.court_count != null ? String(venue.court_count) : "",
            })),
        );
        setWhatsappGroupLink(t.whatsapp_group_link || "");
        const sponsors = splitSponsorBanners(
          res.data.sponsor_banners,
          Boolean(t.sponsor_banner_url || t.player_banner_url),
        );
        setSponsorFirstName(sponsors.firstName);
        setSponsorFirstLink(sponsors.firstLink);
        setExtraSponsors(sponsors.extra);
        setStartDate(t.start_date || "");
        setEndDate(t.end_date || "");
        setRegistrationDeadline(t.registration_deadline || "");
        const drawPlan = drawCeremonyPlanFromPolicy(res.data.settings?.publication_policy_json ?? null);
        setDrawCeremonyEnabled(drawPlan.enabled);
        setDrawCeremonyAt(drawPlan.scheduled_at ?? "");
        setRegistrationCost(t.registration_cost || "");
        // Candado de comprobantes: los torneos nuevos nacen con provider
        // "manual" por default del servidor, pero sin permiso la única
        // opción real es Mercado Pago — se preselecciona para que el
        // guardado ya persista la forma correcta.
        const manualAllowed = registrationSettings.manual_payments_allowed === true;
        const loadedProvider = registrationSettings.provider || "manual";
        setManualPaymentsAllowed(manualAllowed);
        setPaymentProvider(
          !manualAllowed && loadedProvider === "manual"
            ? "mercadopago_marketplace"
            : loadedProvider,
        );
        setRegistrationPlatformFee(registrationSettings.platform_fee_amount || "0");
        setGuardianFieldsAutoEnabled(registrationSettings.guardian_fields_auto_enabled === true);
        setCredentialEnabled(registrationSettings.credential_enabled !== false);
        setMemberFee(registrationSettings.member_fee_amount ?? "");
        setCoachFee(registrationSettings.coach_fee_amount ?? "");
        setAdditionalCategoryFee(registrationSettings.additional_category_fee_amount ?? "");
        setMemberFeeLabel(registrationSettings.member_fee_label ?? "");
        setCoachFeeLabel(registrationSettings.coach_fee_label ?? "");
        setBankAccounts(paymentsRes.data.bank_accounts ?? []);
        const loadedAccountId = registrationSettings.collection_bank_account_id
          ? String(registrationSettings.collection_bank_account_id)
          : "";
        setLoadedBankAccountId(loadedAccountId);
        setLoadedPayoutLabel(registrationSettings.payout_account_label ?? null);
        const collectionAccess = registrationRes.data.collection_account;
        setCurrentCollectionAccount(collectionAccess?.account ?? null);
        // Sin el dato (servidor anterior) se comporta como siempre.
        setCanChangeCollectionAccount(collectionAccess ? collectionAccess.can_change : true);
        setCollectionPayments(collectionAccess?.payments ?? null);
        // Cuentas de cobro F1: las opciones son las cuentas del DUEÑO del
        // torneo (también para el superadmin); sin cuenta elegida se
        // propone su predeterminada.
        if (collectionAccess?.can_change && collectionAccess.options) {
          setBankAccounts(collectionAccess.options);
        }
        const defaultAccountId =
          collectionAccess?.can_change && collectionAccess.default_account_id
            ? String(collectionAccess.default_account_id)
            : "";
        setSelectedBankAccountId(loadedAccountId || defaultAccountId);
        setOfficialBall(t.official_ball || "");
        setOrganizerName(t.organizer_name || "");
        setPosterUrl(t.logo_url);
        setOrganizerLogoUrl(t.organizer_logo_url);
        setSponsorBannerUrl(t.sponsor_banner_url ?? null);
        setPlayerBannerUrl(t.player_banner_url ?? null);
        setSeparateBanners((t.sponsor_banner_url ?? null) !== (t.player_banner_url ?? null));
        setIsPublic(Boolean(t.is_public));
        setIsFederationOfficial(Boolean(t.is_federation_official));
        const notice =
          t.status === "draft" ? copyNoticeFromPolicy(res.data.settings?.publication_policy_json ?? null) : null;
        setCopyNotice(notice);
        setCallPdfUrl(t.call_pdf_url ?? null);
        setRankingLinkedNow(false);
        if (notice?.ranking_linked) {
          // Aparte del cargado de la página: si falla, el renglón se queda.
          api.rankings
            .tournamentLinks(token, tournamentId)
            .then((links) => setRankingLinkedNow((links.data.links ?? []).length > 0))
            .catch(() => setRankingLinkedNow(false));
        }
        initialIdentityRef.current = {
          name: (t.name || "").trim(),
          organizerName: (t.organizer_name || "").trim(),
          organizerLogoUrl: t.organizer_logo_url,
        };
      })
      .catch((err) => {
        setError(
          err instanceof ApiError
            ? err.message
            : "No se pudo cargar el torneo."
        );
      })
      .finally(() => setLoading(false));
  }, [isCreate, token, tournamentId]);

  useEffect(() => {
    if (!isCreate) return;
    setManualPaymentsAllowed(createManualAllowed);
    if (!createManualAllowed) {
      setPaymentProvider((current) => (current === "manual" ? "mercadopago_marketplace" : current));
    }
  }, [createManualAllowed, isCreate]);

  // «Editar categorías» / «Editar forma de cobro» desde la Convocatoria
  // llegan con #categorias / #inscripcion-y-cobro: ya cargado, se baja a esa
  // tarjeta (scroll-margin-top la deja debajo de las barras fijas).
  useEffect(() => {
    if (loading || !JUMP_TARGETS.has(location.hash)) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [loading, location.hash]);

  // ---- categorías en borrador (solo alta) ----
  function updateDivisionForm<K extends keyof TournamentDivisionDraft>(key: K, value: TournamentDivisionDraft[K]) {
    setDivisionForm((current) => ({ ...current, [key]: value }));
  }

  function handleAddDivisionDraft() {
    const draftName = divisionForm.name.trim();
    if (!draftName) {
      setError("Escribe el nombre de la categoría.");
      return;
    }
    if (divisionForm.age_min && divisionForm.age_max && Number(divisionForm.age_min) > Number(divisionForm.age_max)) {
      setError("El año de nacimiento mínimo no puede ser mayor que el año de nacimiento máximo.");
      return;
    }
    const draft = sport === "padel" ? { ...divisionForm, play_mode: "doubles" as const } : divisionForm;
    setDivisionDrafts((current) => [...current, divisionInputFromDraft(draft, current.length)]);
    setDivisionForm(emptyDivisionDraft);
    setError(null);
  }

  // Enter dentro del formulario de la categoría la AGREGA: antes enviaba todo
  // el Paso 1 y la categoría tecleada nunca se agregaba.
  function handleDivisionFormKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Enter") return;
    if ((event.target as HTMLElement).tagName !== "INPUT") return;
    event.preventDefault();
    handleAddDivisionDraft();
  }

  function removeDivisionDraft(index: number) {
    setDivisionDrafts((current) => current.filter((_, currentIndex) => currentIndex !== index));
  }

  function handleSportChange(next: SportKey) {
    if (divisionDrafts.length > 0) {
      setError("Borra las categorías antes de cambiar de deporte");
      return;
    }
    setSport(next);
    if (next === "padel") {
      // El padel es siempre en parejas: al elegirlo, las categorías ya
      // agregadas y el formulario en construcción pasan a dobles para que
      // lo visible coincida con lo que se enviará al backend.
      setDivisionDrafts((current) =>
        current.map((division) => ({ ...division, play_mode: "doubles" as const })),
      );
      setDivisionForm((current) => ({ ...current, play_mode: "doubles" }));
    }
    setError(null);
  }

  // ---- archivos: en el alta se guardan en memoria; al editar se suben ya ----
  // Cambiar de torneo (o salir) invalida lo pendiente: la respuesta de A no
  // toca imágenes, avisos ni botones de B.
  useEffect(() => {
    const seq = imageSeqRef.current;
    const busy = imageBusyRef.current;
    const confirmAbort = new AbortController();
    imageConfirmAbortRef.current = confirmAbort;
    setImageBusy({});
    setImageError(null);
    return () => {
      for (const slot of IMAGE_SLOTS) seq[slot] += 1;
      busy.clear();
      confirmAbort.abort();
    };
  }, [tournamentId]);

  /** Toma el candado de las imágenes; null si alguna ya tiene algo en curso. */
  function beginImageOp(slots: ImageSlot[], kind: ImageOpKind): Record<string, number> | null {
    if (slots.some((slot) => imageBusyRef.current.has(slot))) return null;
    setImageError(null);
    const tickets: Record<string, number> = {};
    for (const slot of slots) {
      imageBusyRef.current.add(slot);
      imageSeqRef.current[slot] += 1;
      tickets[slot] = imageSeqRef.current[slot];
    }
    setImageBusy((current) => {
      const next = { ...current };
      for (const slot of slots) next[slot] = kind;
      return next;
    });
    return tickets;
  }

  function imageOpCurrent(slot: ImageSlot, tickets: Record<string, number>) {
    return imageSeqRef.current[slot] === tickets[slot];
  }

  /** Aplica SOLO el campo de esa imagen y solo si la operación sigue vigente. */
  function applyImageSlot(slot: ImageSlot, tickets: Record<string, number>, t: ImageSlotTournament) {
    if (!imageOpCurrent(slot, tickets)) return;
    if (slot === "logo") setPosterUrl(t.logo_url);
    else if (slot === "organizer-logo") setOrganizerLogoUrl(t.organizer_logo_url);
    else if (slot === "sponsor-banner") setSponsorBannerUrl(t.sponsor_banner_url ?? null);
    else setPlayerBannerUrl(t.player_banner_url ?? null);
  }

  function endImageOp(slots: ImageSlot[], tickets: Record<string, number>) {
    const done = slots.filter((slot) => imageOpCurrent(slot, tickets));
    if (done.length === 0) return;
    for (const slot of done) imageBusyRef.current.delete(slot);
    setImageBusy((current) => {
      const next = { ...current };
      for (const slot of done) delete next[slot];
      return next;
    });
  }

  async function handleUploadPoster(file: File | null) {
    if (!file) return;
    if (isCreate) {
      setPendingPoster(file);
      return;
    }
    if (!token) return;
    const tickets = beginImageOp(["logo"], "upload");
    if (!tickets) return;
    setError(null);
    try {
      const response = await api.tournaments.uploadLogo(token, tournamentId, file);
      applyImageSlot("logo", tickets, response.data.tournament);
    } catch (err) {
      if (imageOpCurrent("logo", tickets)) {
        setError(err instanceof ApiError ? err.message : "No se pudo subir el póster.");
      }
    } finally {
      endImageOp(["logo"], tickets);
    }
  }

  async function handleUploadOrganizerLogo(file: File | null) {
    if (!file) return;
    if (isCreate) {
      setPendingOrganizerLogo(file);
      return;
    }
    if (!token) return;
    const tickets = beginImageOp(["organizer-logo"], "upload");
    if (!tickets) return;
    setError(null);
    try {
      const response = await api.tournaments.uploadOrganizerLogo(token, tournamentId, file);
      applyImageSlot("organizer-logo", tickets, response.data.tournament);
    } catch (err) {
      if (imageOpCurrent("organizer-logo", tickets)) {
        setError(err instanceof ApiError ? err.message : "No se pudo subir el logo del organizador.");
      }
    } finally {
      endImageOp(["organizer-logo"], tickets);
    }
  }

  // Quitar imagen (Eduardo 2026-10-26): igual que «Cambiar», se aplica al
  // instante (no espera a «Guardar cambios»). Solo desvincula el archivo.
  // El banner unificado son DOS imágenes: cada DELETE que funciona se aplica
  // por separado y el aviso dice cuál falló; reintentar es seguro (el
  // servidor deja en blanco lo que ya estaba en blanco).
  async function handleRemoveImage(slots: ImageSlot[], what: string) {
    if (!token || isCreate) return;
    // La operación (y su número) se registra ANTES de preguntar: si mientras
    // el diálogo está abierto se cambia de torneo o se sale de la página, el
    // número deja de ser vigente y la respuesta del diálogo ya no hace nada.
    const tickets = beginImageOp(slots, "confirm");
    if (!tickets) return;
    const confirmed = await askConfirm(
      `¿Quitar ${what}? Dejará de mostrarse en el link público y en las pantallas del torneo. Puedes subir otra cuando quieras.`,
      { acceptLabel: `Sí, quitar ${what}`, signal: imageConfirmAbortRef.current.signal },
    );
    if (!slots.every((slot) => imageOpCurrent(slot, tickets))) return;
    if (!confirmed) {
      endImageOp(slots, tickets);
      return;
    }
    setImageBusy((current) => {
      const next = { ...current };
      for (const slot of slots) next[slot] = "remove";
      return next;
    });
    const failed: { slot: ImageSlot; message: string }[] = [];
    const removed: ImageSlot[] = [];
    try {
      for (const slot of slots) {
        try {
          const t = (await api.tournaments.removeImage(token, tournamentId, slot)).data.tournament;
          applyImageSlot(slot, tickets, t);
          removed.push(slot);
        } catch (err) {
          failed.push({ slot, message: err instanceof ApiError ? err.message : "" });
        }
      }
    } finally {
      endImageOp(slots, tickets);
    }
    if (failed.length === 0 || !slots.every((slot) => imageSeqRef.current[slot] === tickets[slot])) return;
    const detail = failed.map((f) => f.message).filter(Boolean)[0];
    const failedText = failed.map((f) => REMOVE_SURFACE[f.slot]).join(" ni ");
    const prefix = removed.length > 0
      ? `Se quitó ${removed.map((slot) => REMOVE_SURFACE[slot]).join(" y ")}, pero no ${failedText}.`
      : `No se pudo quitar ${failed.length > 1 ? what : failedText}.`;
    setImageError(`${prefix}${detail ? ` ${detail}` : ""} Toca «Quitar» otra vez para reintentar.`);
  }

  function renderRemoveButton(url: string | null, slots: ImageSlot[], label: string, what: string) {
    if (!url || isCreate) return null;
    const removing = slots.some((slot) => imageBusy[slot] === "remove");
    return (
      <button
        type="button"
        className="btn btn--ghost btn--compact wiz-remove"
        disabled={slots.some((slot) => imageBusy[slot]) || submitting}
        aria-busy={removing || undefined}
        onClick={() => { void handleRemoveImage(slots, what); }}
      >
        {removing ? "Quitando…" : label}
      </button>
    );
  }

  // Banners de patrocinador (Eduardo 2026-08-21; unificados QA Cowork #11):
  // "both" sube la MISMA imagen a los dos slots — sin procesado de imagen en
  // servidor, cada superficie la muestra completa y escalada.
  async function handleUploadBanner(slot: "sponsor" | "player" | "both", file: File | null) {
    if (!file) return;
    if (isCreate) {
      if (slot !== "player") setPendingSponsorBanner(file);
      if (slot !== "sponsor") setPendingPlayerBanner(file);
      return;
    }
    if (!token) return;
    const slots: ImageSlot[] =
      slot === "both" ? ["sponsor-banner", "player-banner"] : slot === "sponsor" ? ["sponsor-banner"] : ["player-banner"];
    const tickets = beginImageOp(slots, "upload");
    if (!tickets) return;
    setError(null);
    try {
      if (slot !== "player") {
        const response = await api.tournaments.uploadSponsorBanner(token, tournamentId, file);
        applyImageSlot("sponsor-banner", tickets, response.data.tournament);
      }
      if (slot !== "sponsor") {
        const response = await api.tournaments.uploadPlayerBanner(token, tournamentId, file);
        applyImageSlot("player-banner", tickets, response.data.tournament);
      }
    } catch (err) {
      if (slots.every((s) => imageOpCurrent(s, tickets))) {
        const baseMessage = err instanceof ApiError ? err.message : "No se pudo subir el banner.";
        setError(
          slot === "both"
            ? `${baseMessage} Revisa cada superficie e intenta otra vez.`
            : baseMessage,
        );
      }
    } finally {
      endImageOp(slots, tickets);
    }
  }

  // Patrocinadores extra: al editar, la imagen se sube en cuanto se elige
  // (queda en el torneo al guardar); en el alta se guarda en memoria y se
  // sube en cuanto existe el torneo.
  async function handleUploadExtraSponsor(index: number, file: File | null) {
    if (!file) return;
    if (isCreate) {
      setExtraSponsors((current) => current.map((row, i) => (i === index ? { ...row, pendingFile: file } : row)));
      return;
    }
    if (!token) return;
    setUploadingExtraSponsor(index);
    setError(null);
    try {
      const response = await api.tournaments.uploadExtraSponsorBanner(token, tournamentId, file);
      setExtraSponsors((current) =>
        current.map((row, i) => (i === index ? { ...row, image_url: response.data.image_url, pendingFile: null } : row)),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo subir la imagen del patrocinador.");
    } finally {
      setUploadingExtraSponsor(null);
    }
  }

  function updateExtraSponsor(index: number, patch: Partial<SponsorRowDraft>) {
    setExtraSponsors((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }


  /** Guarda patrocinadores y grupos extra (cada lista con su PUT). En el
   * alta sube antes las imágenes pendientes. Regresa lo que falló, con el
   * motivo del servidor, para decirlo tal cual. */
  async function saveExtras(targetId: number): Promise<string[]> {
    if (!token) return [];
    const failures: string[] = [];
    let rows = extraSponsors;
    if (rows.some((row) => row.pendingFile)) {
      const uploaded: SponsorRowDraft[] = [];
      for (const row of rows) {
        if (!row.pendingFile) {
          uploaded.push(row);
          continue;
        }
        try {
          const response = await api.tournaments.uploadExtraSponsorBanner(token, targetId, row.pendingFile);
          uploaded.push({ ...row, image_url: response.data.image_url, pendingFile: null });
        } catch (err) {
          failures.push(`imagen del patrocinador «${row.name.trim() || row.pendingFile.name}» (${apiErrorDetail(err)})`);
        }
      }
      rows = uploaded;
      setExtraSponsors(uploaded);
    }
    const sponsorPayload = sponsorBannersPayload(sponsorFirstName, sponsorFirstLink, rows);
    if (!isCreate || sponsorPayload.first_name || sponsorPayload.first_link || sponsorPayload.extra.length > 0) {
      try {
        await api.tournaments.saveSponsorBanners(token, targetId, sponsorPayload);
      } catch (err) {
        failures.push(`patrocinadores (${apiErrorDetail(err)})`);
      }
    }
    return failures;
  }

  function validateCommonFields(): string | null {
    if (!name.trim()) return "Escribe el nombre del torneo antes de continuar.";
    if (!isValidOptionalUrl(googleMapsLink)) return "El link de Google Maps debe iniciar con http:// o https://.";
    const sponsorError = sponsorRowsError(sponsorFirstLink, extraSponsors, hasFirstSponsor);
    if (sponsorError) return sponsorError;
    if (startDate && endDate && endDate < startDate) return "La fecha fin no puede ser anterior a la fecha inicio.";
    if (startDate && registrationDeadline && registrationDeadline > startDate) {
      return "La fecha cierre de inscripciones no puede ser posterior a la fecha inicio.";
    }
    return null;
  }

  /** Etiqueta de la cuenta elegida. La que el torneo ya tenía puede no estar
   * en «mis cuentas» (es del organizador): conserva su banco/etiqueta en vez
   * de borrarla al guardar. */
  function selectedPayoutAccountLabel(): string | null {
    const own = bankAccounts.find((account) => String(account.id) === selectedBankAccountId);
    if (own) return own.bank_name;
    if (selectedBankAccountId && selectedBankAccountId === loadedBankAccountId) {
      return currentCollectionAccount?.bank_name ?? loadedPayoutLabel;
    }
    return null;
  }

  const selectedCollectionAccount: PaymentBankAccount | null =
    bankAccounts.find((account) => String(account.id) === selectedBankAccountId) ??
    (selectedBankAccountId && selectedBankAccountId === loadedBankAccountId ? currentCollectionAccount : null);
  const collectionPaymentsCount = (collectionPayments?.paid ?? 0) + (collectionPayments?.in_process ?? 0);

  function registrationSettingsPayload(platformFee: string) {
    return {
      currency: "MXN",
      entry_fee_amount: registrationAmountForSettings(registrationCost),
      // PUT semántico: vacío = null = limpiar el costo diferenciado.
      member_fee_amount: memberFee.trim() === "" ? null : registrationAmountForSettings(memberFee),
      coach_fee_amount: coachFee.trim() === "" ? null : registrationAmountForSettings(coachFee),
      // Vacío = null = cada categoría a precio completo (como siempre).
      additional_category_fee_amount: (() => {
        const parsed = parseAdditionalCategoryFee(additionalCategoryFee);
        // La validación de handleSubmit ya bloqueó lo inválido.
        return parsed.ok ? parsed.value : null;
      })(),
      // "" = volver a la etiqueta de siempre.
      member_fee_label: memberFeeLabel.trim(),
      coach_fee_label: coachFeeLabel.trim(),
      platform_fee_amount: platformFee || "0",
      provider: paymentProvider,
      payout_account_label: paymentProvider === "mercadopago_marketplace" ? "Mercado Pago" : selectedPayoutAccountLabel(),
      collection_bank_account_id: paymentProvider === "manual" && selectedBankAccountId ? Number(selectedBankAccountId) : null,
      notes: paymentProvider === "mercadopago_marketplace"
        ? "Cobro en línea con Mercado Pago."
        : "Transferencia bancaria / comprobante manual.",
    };
  }

  async function handleCreate() {
    if (!token) return;
    const venuesResult = prepareAdditionalVenues(additionalVenues, venueName);
    if (!venuesResult.ok) {
      setError(venuesResult.error);
      return;
    }
    setSubmitting(true);
    let newId: number;
    try {
      const response = await api.tournaments.create(token, {
        name: name.trim(),
        tournament_type: "ladder",
        sport,
        play_mode: sport === "padel"
          ? "doubles"
          : ((divisionDrafts[0]?.play_mode ?? divisionForm.play_mode ?? "singles") as "singles" | "doubles"),
        venue_name: venueName.trim() || null,
        google_maps_link: googleMapsLink.trim() || null,
        start_date: startDate || null,
        end_date: endDate || null,
        registration_deadline: registrationDeadline || null,
        registration_cost: registrationCost.trim() || null,
        official_ball: officialBall.trim() || null,
        is_public: isPublic,
        is_federation_official: isFederationOfficial,
      });
      newId = response.data.tournament.id;
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo crear el torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
      setSubmitting(false);
      return;
    }

    // Desde aquí el torneo YA existe: reintentar el formulario crearía un
    // duplicado. Cada pieza se guarda por separado y, si alguna falla, se
    // dice cuál y por qué (antes un solo error tumbaba todo lo de después
    // y las categorías capturadas se perdían — torneo 246, 2026-10-01).
    const failures: string[] = [];
    async function step(label: string, run: () => Promise<unknown>) {
      try {
        await run();
      } catch (err) {
        failures.push(`${label} (${apiErrorDetail(err)})`);
      }
    }
    // 1) Categorías primero: son lo que más trabajo cuesta capturar.
    for (const [index, division] of divisionDrafts.entries()) {
      await step(`categoría «${division.name}»`, () =>
        api.tournaments.createDivision(token, newId, {
          ...division,
          // Forzado final: handleSportChange y handleAddDivisionDraft ya
          // mantienen los drafts en dobles con padel, pero el submit no debe
          // confiar en que el estado llegó limpio (el backend rechazaría con
          // 409 a mitad del wizard).
          play_mode: sport === "padel" ? "doubles" : (division.play_mode ?? "singles"),
          sort_order: index,
        }),
      );
    }
    if (organizerName.trim()) {
      await step("nombre de la empresa organizadora", () =>
        api.tournaments.update(token, newId, { organizer_name: organizerName.trim() }),
      );
    }
    if (drawCeremonyEnabled) {
      await step("sorteo público del cuadro", () =>
        api.tournaments.saveDrawCeremonyPlan(token, newId, {
          enabled: true,
          scheduled_at: drawCeremonyAt || null,
        }),
      );
    }
    // El torneo recién creado HEREDÓ el fee de plataforma del default
    // global (backend). Este PUT inicial del wizard debe RE-MANDAR ese
    // valor, no un "0" que lo pisaría — para un creador superadmin el
    // backend aplica lo que mande (blocker del gate de #170).
    // Soporte Torneos123 no guarda la forma de cobro (dinero): queda la de
    // fábrica y el organizador la configura.
    if (!isSupport) await step("forma de cobro", async () => {
      const createdSettings = await api.registrations.get(token, newId);
      await api.registrations.updateSettings(
        token,
        newId,
        registrationSettingsPayload(createdSettings.data.settings.platform_fee_amount || "0"),
      );
    });
    if (venuesResult.venues.length > 0) {
      await step("sedes adicionales", () => api.tournaments.updateVenues(token, newId, { venues: venuesResult.venues }));
    }
    // Archivos guardados en memoria durante el alta: ya con id se suben.
    const poster = pendingPoster;
    const organizerLogo = pendingOrganizerLogo;
    const sponsorBanner = pendingSponsorBanner;
    const playerBanner = pendingPlayerBanner;
    if (poster) await step("póster", () => api.tournaments.uploadLogo(token, newId, poster));
    if (organizerLogo) {
      await step("logo del organizador", () => api.tournaments.uploadOrganizerLogo(token, newId, organizerLogo));
    }
    if (sponsorBanner) {
      await step("banner de patrocinador", () => api.tournaments.uploadSponsorBanner(token, newId, sponsorBanner));
    }
    if (playerBanner) {
      await step("banner de patrocinador (vista del jugador)", () =>
        api.tournaments.uploadPlayerBanner(token, newId, playerBanner),
      );
    }
    failures.push(...(await saveExtras(newId)));
    setSubmitting(false);
    if (failures.length > 0) {
      await showNotice(
        "El torneo se creó, pero esto no se pudo guardar:\n" +
          failures.map((failure) => `• ${failure}`).join("\n") +
          "\nRevisa el Paso 1 y vuelve a guardar.",
      );
      navigate(`/admin/tournaments/${newId}/edit`, { replace: true });
      return;
    }
    navigate(`/admin/tournaments/${newId}/setup/rules`, { replace: true });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    setError(null);
    const validation = validateCommonFields();
    if (validation) {
      setError(validation);
      return;
    }
    const additionalFeeCheck = parseAdditionalCategoryFee(additionalCategoryFee);
    if (!additionalFeeCheck.ok) {
      setError(additionalFeeCheck.error);
      return;
    }
    // Una categoría escrita sin «Agregar categoría» (o una edición sin
    // guardar) se perdía sin aviso al salir del Paso 1 (torneo 246).
    const pendingCategory = isCreate
      ? divisionForm.name.trim()
        ? `Tienes la categoría «${divisionForm.name.trim()}» escrita sin agregar: dale «Agregar categoría» o borra el nombre.`
        : null
      : pendingCategoryWarning;
    if (pendingCategory) {
      setError(pendingCategory);
      return;
    }
    if (isCreate) {
      await handleCreate();
      return;
    }
    if (Number.isNaN(tournamentId)) return;
    // Sedes adicionales: validar ANTES de tocar la API — si hay error no se
    // guarda nada (ni datos ni sedes), el guardado es todo-o-nada visible.
    const venuesResult = prepareAdditionalVenues(additionalVenues, venueName);
    if (!venuesResult.ok) {
      setError(venuesResult.error);
      return;
    }
    setSubmitting(true);
    try {
      await api.tournaments.update(token, tournamentId, {
        name: name.trim(),
        venue_name: venueName.trim() || null,
        google_maps_link: googleMapsLink.trim() || null,
        start_date: startDate || null,
        end_date: endDate || null,
        registration_deadline: registrationDeadline || null,
        registration_cost: registrationCost.trim() || null,
        official_ball: officialBall.trim() || null,
        organizer_name: organizerName.trim() || null,
        is_public: isPublic,
        is_federation_official: isFederationOfficial,
      });
      await api.tournaments.saveDrawCeremonyPlan(token, tournamentId, {
        enabled: drawCeremonyEnabled,
        scheduled_at: drawCeremonyEnabled && drawCeremonyAt ? drawCeremonyAt : null,
      });
      // El orden importa: primero los datos del torneo (la validación de
      // duplicados del PUT compara contra la sede principal YA guardada —
      // renombrar la principal y reusar el nombre viejo como adicional debe
      // funcionar en un solo guardado). Después cada pieza se guarda por su
      // lado y, si alguna falla, el aviso dice TODAS las que no quedaron
      // (antes una falla de patrocinadores saltaba la forma de cobro y el
      // aviso no lo decía). Re-guardar es idempotente.
      const failures: string[] = [];
      try {
        await api.tournaments.updateVenues(token, tournamentId, {
          venues: venuesResult.venues,
        });
      } catch (err) {
        failures.push(`sedes adicionales (${apiErrorDetail(err)})`);
      }
      // Soporte Torneos123 no toca la forma de cobro ni los costos: se salta
      // (el Paso 1 re-manda el costo normal sin cambio y el servidor lo acepta).
      if (!isSupport) {
        try {
          await api.registrations.updateSettings(token, tournamentId, registrationSettingsPayload(registrationPlatformFee));
        } catch (err) {
          failures.push(`forma de cobro y costos (${apiErrorDetail(err)})`);
        }
      }
      failures.push(...(await saveExtras(tournamentId)));
      if (failures.length > 0) {
        setError(`Los datos del torneo se guardaron, pero esto no: ${failures.join("; ")}. Corrige y vuelve a guardar.`);
        return;
      }
      navigate(`/admin/tournaments/${tournamentId}/setup/rules`, {
        replace: true,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo guardar el torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <>
        <TournamentSetupProgress current={1} />
        <section className="card">
          <p className="muted">Cargando datos del torneo…</p>
        </section>
      </>
    );
  }

  const pendingFileLabel = (file: File | null) => (file ? `Listo para subir: ${file.name}` : null);
  // Renglones del aviso «Copia de …»: solo lo que sigue pendiente.
  const copyNoticeLines = copyNotice
    ? [
        copyNotice.whatsapp_groups_removed > 0 && !whatsappGroupLink.trim()
          ? "Los grupos de WhatsApp del torneo anterior no se copiaron: agrega el link del grupo de este torneo en el paso Comunicación."
          : null,
        copyNotice.ranking_linked && !rankingLinkedNow
          ? "El torneo anterior sumaba puntos a un ranking: si este también, lígalo en el paso 7, Ranking."
          : null,
        // No hay forma de saber si ya se revisaron: se queda como recordatorio
        // mientras la copia sea borrador, sin decir que falta algo.
        copyNotice.birth_year_shift
          ? "Recordatorio: el torneo anterior era de otro año y las categorías se copiaron con sus mismos años de nacimiento. Si ya los revisaste, no tienes que hacer nada más."
          : null,
        copyNotice.call_pdf_removed && !(callPdfUrl ?? "").trim()
          ? "El PDF propio de la convocatoria no se copió porque traía las fechas anteriores: si usas uno, sube el nuevo en el paso 3, Convocatoria."
          : null,
        // El servidor no copia la forma de cobro cuando el original lo creó
        // otra persona (a ella le pagaba Mercado Pago). Sin este renglón el
        // organizador no se enteraba de que cambió de transferencia a
        // Mercado Pago. Recordatorio mientras la copia sea borrador.
        copyNotice.payment_setup_reset
          ? "La forma de cobro no se copió porque el torneo anterior lo creó otra persona: elige en «Forma de cobro» de este paso cómo vas a cobrar. Para cobrar con transferencia y comprobante necesitas la autorización de Torneos123. Si ya la elegiste, no tienes que hacer nada más."
          : null,
        copyNotice.platform_fee_review
          ? "Torneos123 va a revisar su comisión por inscripción en este torneo porque alguno de tus costos es menor que la comisión normal. No tienes que hacer nada."
          : null,
      ].filter((line): line is string => line !== null)
    : [];
  // Revisión ronda 5: el link final de la copia (con las letras que el
  // servidor agrega si otro torneo ya usaba ese link) se ve siempre en el
  // aviso, no solo al cambiar el nombre.
  const copyLinkSuffix =
    copyNotice && publicSlug && initialIdentityRef.current !== null
      ? registrationLinkCollisionSuffix(publicSlug, initialIdentityRef.current.name)
      : null;

  return (
    <div className="wiz-step wiz-step--datos">
      <TournamentSetupProgress current={1} />
      <section className="wiz-hero wiz-hero--navy">
        <div>
          <p className="eyebrow">{name.trim() ? `Paso 1 de 10 · ${name.trim()}` : "Paso 1 de 10"}</p>
          <h1>Datos del torneo</h1>
          <p className="wiz-hero__lead">
            {isCreate
              ? "Captura la identidad, calendario y datos públicos que después se reutilizan en la convocatoria."
              : "Ajusta la identidad, calendario y datos públicos que alimentan la convocatoria."}
          </p>
        </div>
      </section>

      {copyNotice ? (
        <section className="card tournament-closed-banner tournament-copy-notice" data-no-toast role="status">
          {/* Con el póster ya subido solo queda revisar las fechas: si no, el
              organizador creería que la subida no funcionó. */}
          <strong>
            {posterUrl || pendingPoster
              ? `Copia de «${copyNotice.name}»: revisa las fechas.`
              : `Copia de «${copyNotice.name}»: sube el póster nuevo y revisa las fechas.`}
          </strong>
          {publicSlug ? (
            <p className="tournament-copy-notice__link">
              {`Link de inscripción de este torneo: ${REGISTRATION_LINK_PREFIX}${publicSlug}`}
              {copyLinkSuffix ? `. Termina en «${copyLinkSuffix}» porque otro torneo ya usaba ese link.` : null}
            </p>
          ) : null}
          {copyNoticeLines.length > 0 ? (
            <ul>
              {copyNoticeLines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      <form className="setup-call-layout setup-data-layout" onSubmit={handleSubmit} noValidate>
        <div className="wiz-cols">
        <div className="wiz-main">
          <section className="card wiz-card">
            <header className="wiz-card__head"><h2>Identidad del torneo</h2><p>Nombre y organizador. La modalidad vive en cada categoría.</p></header>
            <div className="setup-call-grid setup-call-grid--two">
            {isCreate && (
              <label>
                Deporte
                <select
                  value={sport}
                  onChange={(e) => handleSportChange(e.target.value as SportKey)}
                  disabled={submitting}
                >
                  {Object.entries(SPORT_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                {sport === "padel" ? (
                  <span className="muted">El padel se juega en parejas: las categorías serán de dobles.</span>
                ) : null}
              </label>
            )}
            <label className="setup-call-form__wide">
              Nombre
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
              />
              {/* Revisión ronda 4 (duplicar): quien renombra una copia
                  esperaba que el link cambiara con el nombre. */}
              {!isCreate &&
              publicSlug &&
              initialIdentityRef.current !== null &&
              name.trim() !== initialIdentityRef.current.name ? (
                <span className="muted" data-no-toast>
                  El link de inscripción no cambia con el nombre: sigue siendo {REGISTRATION_LINK_PREFIX}
                  {publicSlug}
                </span>
              ) : null}
            </label>
            <label>
              <span className="hint-label">
                Nombre Empresa Organizadora
                <FieldHint>
                  Aparece en el link público, los PDFs y la credencial del jugador.
                </FieldHint>
              </span>
              <input
                type="text"
                value={organizerName}
                onChange={(e) => setOrganizerName(e.target.value)}
                disabled={submitting}
                placeholder="Ej. Club Olympia Hidalgo"
              />
            </label>
            {credentialEnabled && (identityChanged || credentialRegenMessage) && (
              <div className="alert alert--info setup-call-form__wide" role="note">
                <strong>¿Ya habías emitido credenciales?</strong> El logo, la
                empresa y el nombre quedan impresos dentro de cada credencial:
                cambiarlos aquí no actualiza las ya emitidas.
                <div className="form__actions" style={{ marginTop: "0.4rem" }}>
                  <button
                    type="button"
                    className="btn btn--ghost btn--compact"
                    onClick={() => void handleRegenerateCredentials()}
                    disabled={regeneratingCredentials}
                  >
                    {regeneratingCredentials ? "Regenerando…" : "Regenerar credenciales emitidas"}
                  </button>
                </div>
                {credentialRegenMessage && (
                  <p className="form-message" style={{ marginTop: "0.35rem" }}>{credentialRegenMessage}</p>
                )}
              </div>
            )}
            </div>
          </section>
          <section className="card wiz-card">
            <header className="wiz-card__head"><h2>Fechas</h2><p>Alimentan convocatoria e inscripciones.</p></header>
            <div className="setup-call-grid setup-call-grid--dates">
            <label>
              Fecha inicio
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={submitting}
              />
              {startDateNotice ? (
                // Sin clase de error: GlobalToasts levanta los .form-error del
                // DOM como toast y este aviso no nace de una acción fallida.
                <small className="field-warning" role="status">⚠️ {startDateNotice.message}</small>
              ) : null}
            </label>
            <label>
              Fecha fin
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label className="setup-call-form__wide setup-call-field--highlight">
              <span className="hint-label">
                Fecha cierre de inscripciones
                <FieldHint>
                  Los jugadores pueden inscribirse por el link público hasta este
                  día inclusive; al día siguiente el link se cierra solo.
                </FieldHint>
              </span>
              <input
                type="date"
                value={registrationDeadline}
                max={startDate || undefined}
                onChange={(e) => setRegistrationDeadline(e.target.value)}
                disabled={submitting}
              />
            </label>
            {/* Mismo patrón que "Torneo público": el input hereda estilos de
                caja de texto y con la clase propia dibujaba un rectángulo
                fuera del checkbox al enfocarlo (Eduardo 2026-09-11). */}
            <label className="form__checkbox form__checkbox--boxed setup-call-form__wide">
              <input
                type="checkbox"
                checked={drawCeremonyEnabled}
                onChange={(e) => setDrawCeremonyEnabled(e.target.checked)}
                disabled={submitting}
              />
              <span>
                <span className="hint-label">
                  Habrá sorteo público del cuadro
                  <FieldHint>
                    Si lo activas, la fecha y hora del sorteo aparecen en la convocatoria (Formato de
                    competencia, junto con la mecánica estándar internacional) y en la vista del jugador.
                    En el paso Draw se ve en solo lectura; para cambiarla regresas aquí.
                  </FieldHint>
                </span>
              </span>
            </label>
            {drawCeremonyEnabled ? (
              <label className="setup-call-form__wide">
                <span className="hint-label">
                  Fecha y hora del sorteo (hora de la Ciudad de México)
                  <FieldHint>
                    Se anuncia tal cual a los jugadores. Puedes dejarla vacía mientras la defines:
                    la convocatoria dirá "fecha y hora por anunciar".
                  </FieldHint>
                </span>
                <input
                  type="datetime-local"
                  value={drawCeremonyAt}
                  onChange={(e) => setDrawCeremonyAt(e.target.value)}
                  disabled={submitting}
                />
              </label>
            ) : null}
            </div>
          </section>
          <section className="card wiz-card">
            <header className="wiz-card__head"><h2>Sedes</h2></header>
            <div className="setup-call-grid setup-call-grid--two">
            <label>
              Sede
              <input
                type="text"
                value={venueName}
                onChange={(e) => setVenueName(e.target.value)}
                disabled={submitting}
                placeholder="Club / academia / sede"
              />
            </label>
            <label className="setup-call-form__wide">
              Link Google Maps
              <input
                type="url"
                value={googleMapsLink}
                onChange={(e) => setGoogleMapsLink(e.target.value)}
                disabled={submitting}
                placeholder="https://maps.app.goo.gl/..."
              />
            </label>
            {additionalVenues.map((row, index) => (
              <div className="setup-call-form__wide setup-venue-row" key={index}>
                <label>
                  Sede {index + 2}
                  <input
                    type="text"
                    value={row.name}
                    onChange={(e) => setAdditionalVenues((current) => current.map((item, i) => (i === index ? { ...item, name: e.target.value } : item)))}
                    disabled={submitting}
                    placeholder="Club / academia / sede"
                  />
                </label>
                <label>
                  Link Google Maps
                  <input
                    type="url"
                    value={row.google_maps_link}
                    onChange={(e) => setAdditionalVenues((current) => current.map((item, i) => (i === index ? { ...item, google_maps_link: e.target.value } : item)))}
                    disabled={submitting}
                    placeholder="https://maps.app.goo.gl/..."
                  />
                </label>
                {/* Canchas se define en el Calendario (pedido Eduardo
                    2026-08-18); el valor ya capturado se conserva intacto
                    porque el estado de la fila lo sigue cargando y enviando. */}
                <button
                  type="button"
                  className="btn btn--ghost setup-venue-row__remove"
                  onClick={() => setAdditionalVenues((current) => current.filter((_, i) => i !== index))}
                  disabled={submitting}
                  aria-label={`Quitar sede ${index + 2}`}
                >
                  Quitar
                </button>
              </div>
            ))}
            <div className="setup-call-form__wide hint-label">
              <button
                type="button"
                className="btn btn--ghost wiz-add"
                onClick={() => setAdditionalVenues((current) => [...current, emptyVenueRow()])}
                disabled={submitting}
              >
                + Agregar otra sede
              </button>
              <FieldHint label="¿Para qué agregar otra sede?">
                Si el torneo se juega en más de un lugar, agrega cada sede con su
                mapa. La convocatoria pública las muestra todas; las canchas y la
                programación por sede se definen en el calendario.
              </FieldHint>
            </div>
            </div>
          </section>
          <section id="inscripcion-y-cobro" className="card wiz-card setup-data-card">
            <header className="wiz-card__head"><h2>Inscripción y cobro</h2><p>Datos visibles para jugadores y administradores.</p></header>
            {isSupport ? (
              <p className="support-money-note" role="note">
                Como {SUPPORT_ROLE_LABEL} no cambias costos, forma de cobro ni la cuenta a la que llegan los pagos:
                eso lo decide el organizador. El resto del torneo sí lo puedes editar.
              </p>
            ) : null}
          <fieldset className="support-money-lock" disabled={isSupport || undefined}>
          <div className="setup-call-grid setup-call-grid--two">
            <label>
              Costo Normal
              <input
                type="text"
                placeholder="$1,500 MXN"
                value={registrationCost}
                onChange={(e) => setRegistrationCost(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              <span className="hint-label">
                Costo {memberFeeLabel.trim() || "Socio"} (opcional)
                <FieldHint>
                  El jugador que se declare de este tipo paga esto. 0 = gratis; vacío =
                  no se ofrece este precio.
                </FieldHint>
              </span>
              <input
                type="text"
                placeholder="Vacío = no se ofrece"
                value={memberFee}
                onChange={(e) => setMemberFee(e.target.value)}
                disabled={submitting}
              />
              <input
                type="text"
                maxLength={40}
                aria-label="Etiqueta del primer costo diferenciado"
                placeholder="Etiqueta que verá el jugador (Socio)"
                value={memberFeeLabel}
                onChange={(e) => setMemberFeeLabel(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              <span className="hint-label">
                Costo {coachFeeLabel.trim() || "Profesores"} (opcional)
                <FieldHint>
                  Solo sustituyen el costo Normal; una categoría con costo propio
                  lo conserva. Escribe abajo por qué es un costo distinto (p. ej.
                  Afiliados a la Federación).
                </FieldHint>
              </span>
              <input
                type="text"
                placeholder="Vacío = no se ofrece"
                value={coachFee}
                onChange={(e) => setCoachFee(e.target.value)}
                disabled={submitting}
              />
              <input
                type="text"
                maxLength={40}
                aria-label="Etiqueta del segundo costo diferenciado"
                placeholder="Etiqueta que verá el jugador (Profesor)"
                value={coachFeeLabel}
                onChange={(e) => setCoachFeeLabel(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              <span className="hint-label">
                Costo por categoría adicional (opcional)
                <FieldHint>
                  Si un jugador se inscribe en más de una categoría de este torneo,
                  cada categoría extra le cuesta este monto (igual para todos los
                  tipos de costo). Vacío = cada categoría a precio completo.
                </FieldHint>
              </span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="Vacío = precio completo"
                aria-label="Costo por categoría adicional"
                aria-invalid={additionalCategoryFeeError ? true : undefined}
                aria-describedby={additionalCategoryFeeError ? "additional-category-fee-error" : undefined}
                value={additionalCategoryFee}
                onChange={(e) => setAdditionalCategoryFee(e.target.value)}
                disabled={submitting}
              />
              {additionalCategoryFeeError ? (
                <small id="additional-category-fee-error" className="form-help-text additional-category-fee-error" role="alert" data-no-toast>
                  {additionalCategoryFeeError}
                </small>
              ) : null}
            </label>
            <label>
              Pelota oficial
              <input
                type="text"
                placeholder="Wilson US Open / Head Tour / Penn..."
                value={officialBall}
                onChange={(e) => setOfficialBall(e.target.value)}
                disabled={submitting}
              />
            </label>
            <div className="setup-call-form__wide wiz-pay-cards" role="group" aria-label="Forma de cobro">
              <button type="button" className={`wiz-pay-card${paymentProvider === "mercadopago_marketplace" ? " is-selected" : ""}`} aria-pressed={paymentProvider === "mercadopago_marketplace"} onClick={() => setPaymentProvider("mercadopago_marketplace")} disabled={submitting}>
                <strong>Mercado Pago</strong>
                <span>Pago en línea</span>
              </button>
              {manualPaymentsAllowed && (
                <button type="button" className={`wiz-pay-card${paymentProvider === "manual" ? " is-selected" : ""}`} aria-pressed={paymentProvider === "manual"} onClick={() => setPaymentProvider("manual")} disabled={submitting}>
                  <strong>Transferencia</strong>
                  <span>Subir comprobante</span>
                </button>
              )}
            </div>
            <label className="setup-call-form__wide">
              <span className="hint-label">
                Forma de cobro para el link público
                <FieldHint>
                  {manualPaymentsAllowed
                    ? "Esto define qué verá el jugador en el link público: «Transferencia + comprobante» = transferencia bancaria y el jugador sube su comprobante; «Mercado Pago (en línea)» = paga en línea con Mercado Pago."
                    : "Los torneos se cobran en línea con Mercado Pago. El cobro con comprobantes requiere autorización de Torneos123 — pídela al superadmin para casos específicos."}
                </FieldHint>
              </span>
              <select className="t123-select" value={paymentProvider} onChange={(e) => setPaymentProvider(e.target.value)} disabled={submitting}>
                {/* Candado de comprobantes: la opción manual solo se ofrece
                    con permiso del superadmin (los torneos que ya cobraban
                    manual quedaron con el permiso por la migración). El
                    candado real es del servidor. */}
                {manualPaymentsAllowed && (
                  <option value="manual">Transferencia + comprobante</option>
                )}
                <option value="mercadopago_marketplace">Mercado Pago (en línea)</option>
              </select>
            </label>
            {paymentProvider === "manual" ? (
              // Cuentas de cobro F1: ¿a qué cuenta llegan los pagos? Solo el
              // dueño del torneo (o el superadmin) la elige entre las cuentas
              // del DUEÑO; el coorganizador la ve (enmascarada) sin cambiarla.
              <div className="setup-call-form__wide setup-data-collection-account cobro-torneo" data-no-toast>
                <h3 className="cobro-torneo__title">¿A qué cuenta llegan los pagos de este torneo?</h3>
                {collectionPaymentsCount > 0 ? (
                  <p className="cobro-torneo__warn">
                    Este torneo ya tiene pagos ({collectionPayments?.paid ?? 0} pagados, {collectionPayments?.in_process ?? 0} en proceso). El cambio aplica solo a pagos nuevos: lo ya cobrado se queda en la cuenta original.
                  </p>
                ) : null}
                {!canChangeCollectionAccount ? (
                  <>
                    <span>Cuenta bancaria para transferencia</span>
                    <p className="cobro-torneo__readonly">
                      {currentCollectionAccount
                        ? `Cuenta del torneo: ${bankAccountSummary(currentCollectionAccount)}`
                        : "El organizador del torneo todavía no elige la cuenta bancaria."}
                    </p>
                    <small className="muted">
                      Solo el organizador del torneo puede cambiarla. Al guardar, el torneo se queda con esta.
                    </small>
                  </>
                ) : (
                  <>
                    <label className="cobro-torneo__field setup-call-field--highlight">
                      <span className="hint-label">
                        Cuenta bancaria para transferencia
                        <FieldHint>
                          Cada torneo puede cobrar a una cuenta distinta del organizador.{" "}
                          <Link to="/admin/payments">Administrar cuentas</Link>
                        </FieldHint>
                      </span>
                      <select
                        className="t123-select"
                        value={selectedBankAccountId}
                        onChange={(e) => setSelectedBankAccountId(e.target.value)}
                        disabled={submitting}
                      >
                        <option value="">Seleccionar cuenta bancaria</option>
                        {/* La cuenta que el torneo ya tiene aunque ya no esté
                            entre las activas: sin esto el campo se veía vacío
                            y al guardar se perdía su etiqueta. */}
                        {loadedBankAccountId && !bankAccounts.some((account) => String(account.id) === loadedBankAccountId) ? (
                          <option value={loadedBankAccountId}>
                            {currentCollectionAccount && String(currentCollectionAccount.id) === loadedBankAccountId
                              ? `Cuenta actual del torneo: ${bankAccountSummary(currentCollectionAccount)}`
                              : `Cuenta actual del torneo${loadedPayoutLabel ? `: ${loadedPayoutLabel}` : ""}`}
                          </option>
                        ) : null}
                        {bankAccounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {bankAccountSummary(account)}
                            {account.is_default ? " (Predeterminada)" : ""}
                          </option>
                        ))}
                      </select>
                      <small className="muted">
                        El jugador ve banco, CLABE completa, beneficiario y concepto.{" "}
                        <Link to="/admin/payments">Administrar cuentas</Link>
                      </small>
                    </label>
                    {selectedCollectionAccount ? (
                      <div className="cobro-torneo__preview" aria-label="Así lo verá el jugador">
                        <strong>Así lo verá el jugador al pagar por transferencia</strong>
                        <span>
                          {selectedCollectionAccount.bank_name}
                          {selectedCollectionAccount.clabe ? ` · CLABE ${selectedCollectionAccount.clabe}` : ""}
                          {!selectedCollectionAccount.clabe && selectedCollectionAccount.account_number
                            ? ` · Cuenta ${selectedCollectionAccount.account_number}`
                            : ""}
                        </span>
                        <span>Beneficiario: {selectedCollectionAccount.beneficiary_name}</span>
                        {selectedCollectionAccount.payment_concept ? (
                          <span>Concepto: {selectedCollectionAccount.payment_concept}</span>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </div>
          </fieldset>
          <div className="setup-data-option-grid">
            <label className="form__checkbox form__checkbox--boxed">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                disabled={submitting}
              />
              <span>
                <span className="hint-label">
                  Torneo público
                  <FieldHint>
                    Activado, aparece en torneos123.com y en «Torneos
                    públicos» de los jugadores mientras tenga fecha de inicio e
                    inscripciones abiertas. Ojo: el link directo de inscripción
                    funciona aunque esté desactivado — compártelo solo con quien
                    quieras.
                  </FieldHint>
                </span>
                {isPublic && !startDate ? (
                  <small className="muted" data-no-toast>
                    Para aparecer en torneos123.com falta la fecha de inicio.
                  </small>
                ) : null}
              </span>
            </label>
            <label className="form__checkbox form__checkbox--boxed">
              <input
                type="checkbox"
                checked={isFederationOfficial}
                onChange={(e) => setIsFederationOfficial(e.target.checked)}
                disabled={submitting}
              />
              <span>
                <span className="hint-label">
                  Torneo oficial de Federación Mexicana de Tennis
                  <FieldHint>
                    Marca torneos avalados/oficiales para reportes y distintivos.
                  </FieldHint>
                </span>
              </span>
            </label>
          </div>
        </section>
        </div>
        <aside className="wiz-aside">
          <section className="card wiz-card">
            <header className="wiz-card__head"><h2>Imágenes</h2><p>Toca una imagen para cambiarla.</p></header>
            <div className="wiz-images">
            <label>
              <span className="hint-label">
                Logo del organizador
                <FieldHint>
                  Distinto del póster: es el logo de tu club/empresa para el link
                  público, los PDFs y la credencial.
                  {organizerLogoUrl ? " Ya hay uno cargado; subir otro lo reemplaza." : ""}
                  {isCreate ? " Se sube en cuanto guardes el torneo." : ""}
                </FieldHint>
              </span>
              <WizUpload label={organizerLogoUrl ? "Cambiar logo" : "Subir logo"}>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={Boolean(imageBusy["organizer-logo"]) || submitting}
                  onChange={(e) => { void handleUploadOrganizerLogo(e.target.files?.[0] ?? null); e.currentTarget.value = ""; }}
                />
              </WizUpload>
              {uploadingOrganizerLogo && <span className="muted">Subiendo logo…</span>}
              {isCreate && pendingOrganizerLogo && <span className="muted">{pendingFileLabel(pendingOrganizerLogo)}</span>}
              {renderCurrentUpload(organizerLogoUrl, "Logo actual")}
              {renderRemoveButton(organizerLogoUrl, ["organizer-logo"], "Quitar logo", "el logo")}
            </label>
            <label
              className={copyNotice && !posterUrl && !pendingPoster ? "setup-call-field--highlight setup-data-poster--pending" : undefined}
            >
              <span className="hint-label">
                Póster o imagen del torneo
                <FieldHint>
                  La imagen grande del link público, las tarjetas y las pantallas
                  del jugador. Recomendado vertical 1080 × 1350 px (4:5), buena
                  resolución (hasta 5 MB) para que no se vea pixelada.
                  {posterUrl ? " Ya hay una cargada; subir otra la reemplaza." : ""}
                  {isCreate ? " Se sube en cuanto guardes el torneo." : ""}
                </FieldHint>
              </span>
              <WizUpload label={posterUrl ? "Cambiar póster" : "Subir póster"}>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={Boolean(imageBusy.logo) || submitting}
                  onChange={(e) => { void handleUploadPoster(e.target.files?.[0] ?? null); e.currentTarget.value = ""; }}
                />
              </WizUpload>
              {copyNotice && !posterUrl && !uploadingPoster ? (
                <span className="field-warning">Sube el póster del nuevo torneo.</span>
              ) : null}
              {uploadingPoster && <span className="muted">Subiendo póster…</span>}
              {isCreate && pendingPoster && <span className="muted">{pendingFileLabel(pendingPoster)}</span>}
              {renderCurrentUpload(posterUrl, "Póster actual")}
              {renderRemoveButton(posterUrl, ["logo"], "Quitar póster", "el póster")}
            </label>
            <label>
              <span className="hint-label">
                {separateBanners ? "Banner de patrocinador — link público" : "Banner de patrocinador"}
                <FieldHint>
                  {separateBanners
                    ? "Se muestra arriba del link de inscripción, como franja horizontal. Recomendado 970 × 250 px (JPG/PNG/WEBP, hasta 5 MB)."
                    : "Una sola imagen para el link público de inscripción y las pantallas del jugador. Ojo: son espacios de forma distinta — en el link público es una franja horizontal (970 × 250 px) y en la vista del jugador un cuadro vertical junto al póster (1080 × 1350 px), así que una sola imagen se verá chica en uno de los dos. Para que llene ambos, usa imágenes distintas por superficie con el botón de abajo."}
                  {sponsorBannerUrl ? " Ya hay uno cargado; subir otro lo reemplaza." : " Sin banner no se muestra ningún hueco."}
                </FieldHint>
              </span>
              <WizUpload label={sponsorBannerUrl ? "Cambiar banner" : "Subir banner"}>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={Boolean(imageBusy["sponsor-banner"] || (!separateBanners && imageBusy["player-banner"])) || submitting}
                  onChange={(e) => { void handleUploadBanner(separateBanners ? "sponsor" : "both", e.target.files?.[0] ?? null); e.currentTarget.value = ""; }}
                />
              </WizUpload>
              {imageBusy["sponsor-banner"] === "upload" && <span className="muted">Subiendo banner…</span>}
              {isCreate && pendingSponsorBanner && <span className="muted">{pendingFileLabel(pendingSponsorBanner)}</span>}
              {renderCurrentUpload(separateBanners ? sponsorBannerUrl : sponsorBannerUrl || playerBannerUrl, "Banner actual")}
              {separateBanners
                ? renderRemoveButton(sponsorBannerUrl, ["sponsor-banner"], "Quitar banner", "el banner del link público")
                : renderRemoveButton(sponsorBannerUrl || playerBannerUrl, ["sponsor-banner", "player-banner"], "Quitar banner", "el banner")}
              {!separateBanners && (
                <button
                  type="button"
                  className="btn btn--ghost btn--compact"
                  onClick={() => setSeparateBanners(true)}
                >
                  ¿Imágenes distintas por superficie?
                </button>
              )}
            </label>
            {separateBanners && (
              <label>
                <span className="hint-label">
                  Banner de patrocinador — vista del jugador
                  <FieldHint>
                    Se muestra en las pantallas del torneo de cada jugador, a un lado del póster y
                    con la misma altura. Recomendado vertical 1080 × 1350 px (4:5, la misma
                    proporción que el póster) para que los dos llenen la fila.
                    {playerBannerUrl ? " Ya hay uno cargado; subir otro lo reemplaza." : ""}
                  </FieldHint>
                </span>
                <WizUpload label={playerBannerUrl ? "Cambiar banner" : "Subir banner"}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={Boolean(imageBusy["player-banner"]) || submitting}
                    onChange={(e) => { void handleUploadBanner("player", e.target.files?.[0] ?? null); e.currentTarget.value = ""; }}
                  />
                </WizUpload>
                {imageBusy["player-banner"] === "upload" && <span className="muted">Subiendo banner…</span>}
                {isCreate && pendingPlayerBanner && <span className="muted">{pendingFileLabel(pendingPlayerBanner)}</span>}
                {renderCurrentUpload(playerBannerUrl, "Banner actual")}
                {renderRemoveButton(playerBannerUrl, ["player-banner"], "Quitar banner", "el banner de la vista del jugador")}
                <button
                  type="button"
                  className="btn btn--ghost btn--compact"
                  onClick={() => setSeparateBanners(false)}
                  title="La próxima imagen que subas se usará en ambas superficies."
                >
                  Volver a una sola imagen
                </button>
              </label>
            )}
            </div>
            {imageError && (
              <p role="alert" className="form-error wiz-image-error">
                {imageError}
              </p>
            )}
          </section>
          <section className="card wiz-card">
            <header className="wiz-card__head"><h2>Patrocinadores</h2></header>
            <div className="wiz-sponsors">
            {sponsorBannerUrl || playerBannerUrl || pendingSponsorBanner || sponsorFirstName || sponsorFirstLink ? (
              <div className="setup-call-form__wide setup-extra-row setup-extra-row--meta">
                <label>
                  <span className="hint-label">
                    Nombre del patrocinador 1 (opcional)
                    <FieldHint>Se lee en lugar de la imagen cuando no carga y lo usan los lectores de pantalla.</FieldHint>
                  </span>
                  <input
                    type="text"
                    maxLength={80}
                    value={sponsorFirstName}
                    onChange={(e) => setSponsorFirstName(e.target.value)}
                    disabled={submitting}
                    placeholder="Ej. Raquetas del Valle"
                  />
                </label>
                <label>
                  <span className="hint-label">
                    Link del patrocinador 1 (opcional)
                    <FieldHint>Si lo pones, al tocar el banner se abre la página del patrocinador.</FieldHint>
                  </span>
                  <input
                    type="url"
                    value={sponsorFirstLink}
                    onChange={(e) => setSponsorFirstLink(e.target.value)}
                    disabled={submitting}
                    placeholder="https://..."
                  />
                </label>
              </div>
            ) : null}
            {extraSponsors.map((row, index) => (
              <div className="setup-call-form__wide setup-extra-row setup-extra-row--sponsor" key={`patrocinador-${index}`}>
                <label>
                  Patrocinador {index + extraSponsorStart}: imagen
                  <WizUpload label={row.image_url ? "Cambiar imagen" : "Subir imagen"}>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      disabled={uploadingExtraSponsor !== null || submitting}
                      onChange={(e) => { void handleUploadExtraSponsor(index, e.target.files?.[0] ?? null); e.currentTarget.value = ""; }}
                    />
                  </WizUpload>
                  {uploadingExtraSponsor === index && <span className="muted">Subiendo imagen…</span>}
                  {isCreate && row.pendingFile && <span className="muted">{pendingFileLabel(row.pendingFile)}</span>}
                  {renderCurrentUpload(row.image_url, "Imagen actual")}
                </label>
                <label>
                  Nombre (opcional)
                  <input
                    type="text"
                    maxLength={80}
                    value={row.name}
                    onChange={(e) => updateExtraSponsor(index, { name: e.target.value })}
                    disabled={submitting}
                    placeholder="Ej. Bebidas Norte"
                  />
                </label>
                <label>
                  Link (opcional)
                  <input
                    type="url"
                    value={row.link}
                    onChange={(e) => updateExtraSponsor(index, { link: e.target.value })}
                    disabled={submitting}
                    placeholder="https://..."
                  />
                </label>
                <button
                  type="button"
                  className="btn btn--ghost setup-venue-row__remove"
                  onClick={() => setExtraSponsors((current) => current.filter((_, i) => i !== index))}
                  disabled={submitting || uploadingExtraSponsor !== null}
                  aria-label={`Quitar patrocinador ${index + extraSponsorStart}`}
                >
                  Quitar
                </button>
              </div>
            ))}
            <div className="setup-call-form__wide hint-label">
              <button
                type="button"
                className="btn btn--ghost wiz-add"
                onClick={() => setExtraSponsors((current) => [...current, emptySponsorRow()])}
                disabled={submitting || extraSponsors.length + 1 >= MAX_SPONSORS}
              >
                + Agregar otro patrocinador
              </button>
              <FieldHint label="¿Para qué agregar otro patrocinador?">
                Si tienes más de un patrocinador, agrega cada uno con su imagen (JPG/PNG/WEBP hasta
                5 MB) y, si quieres, su nombre y su página. Cada uno lleva una sola imagen para las dos
                superficies: en el link de inscripción se muestran todos como franja (970 × 250 px) y en
                las pantallas del jugador van rotando en el cuadro vertical junto al póster (1080 × 1350
                px), así que una franja ahí se verá chica. Se guardan al dar «Guardar y continuar».
              </FieldHint>
            </div>
            </div>
          </section>
        </aside>
        </div>
        {/* Eduardo 2026-10-12: «Categorías del torneo» va al final de toda la
            pantalla (después de Imágenes y Patrocinadores), también en celular. */}
        <div className="wiz-categories-end">
        {isCreate ? (
          <section className="card setup-call-card setup-data-card tournament-divisions-card tournament-divisions-card--step-one">
            <div className="setup-call-card__heading">
              <span>04</span>
              <div>
                <h2>Categorías del torneo</h2>
                <p>Se usarán en convocatoria y en el formulario público de inscripción.</p>
              </div>
            </div>
            <div className="setup-call-grid setup-call-grid--two tournament-division-form" onKeyDown={handleDivisionFormKeyDown}>
              <label>
                Nombre
                <input value={divisionForm.name} onChange={(e) => updateDivisionForm("name", e.target.value)} placeholder="12 y menores Varonil" />
              </label>
              <label>
                Género
                <select className="t123-select" value={divisionForm.gender} onChange={(e) => updateDivisionForm("gender", e.target.value)}>
                  <option value="">Abierta / sin restricción</option>
                  <option value="male">Varonil</option>
                  <option value="female">Femenil</option>
                  <option value="mixed">Mixto</option>
                </select>
              </label>
              <label>
                Modalidad de esta categoría
                <select
                  className="t123-select"
                  value={divisionModality(divisionForm)}
                  onChange={(e) => setDivisionForm((current) => applyDivisionModality(current, e.target.value as DivisionModality))}
                >
                  {DIVISION_MODALITY_OPTIONS
                    // Pádel siempre en parejas: sin singles.
                    .filter((option) => sport !== "padel" || option.value !== "singles")
                    .map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                {divisionForm.team_play ? (
                  <span className="muted">Se inscriben parejas; cada enfrentamiento se juega en líneas. Las líneas y la alineación se afinan en el Paso 5.</span>
                ) : null}
              </label>
              <label>
                <span className="hint-label">
                  Nacidos desde (año)
                  <FieldHint>Año de nacimiento más antiguo que acepta la categoría: los mayores del grupo. Ej. "10 y menores" en 2026 → 2016. Se evalúa por año calendario del torneo, no por cumpleaños.</FieldHint>
                </span>
                <input type="number" min="1900" max="2100" placeholder="Ej. 2016" value={divisionForm.age_min} onChange={(e) => updateDivisionForm("age_min", e.target.value)} />
              </label>
              <label>
                <span className="hint-label">
                  Nacidos hasta (año)
                  <FieldHint>Año de nacimiento más reciente que acepta la categoría: los más chicos del grupo. Ej. "de 8 a 10 años" en 2026 → 2018. En una "X y menores" puedes dejarlo vacío (acepta a los más chicos). Ambos vacíos = sin límite de edad.</FieldHint>
                </span>
                <input type="number" min="1900" max="2100" placeholder="Ej. 2018" value={divisionForm.age_max} onChange={(e) => updateDivisionForm("age_max", e.target.value)} />
              </label>
              <BirthYearFeedback
                name={divisionForm.name}
                ageMin={divisionForm.age_min}
                ageMax={divisionForm.age_max}
                tournamentYear={tournamentYear}
              />
              <label>
                Nivel
                <input value={divisionForm.level_label} onChange={(e) => updateDivisionForm("level_label", e.target.value)} placeholder="A, B, Intermedios…" />
              </label>
              <label>
                Costo específico
                <input
                  value={divisionForm.registration_cost}
                  onChange={(e) => updateDivisionForm("registration_cost", e.target.value)}
                  placeholder="Opcional, ej. $700"
                  disabled={isSupport}
                  title={isSupport ? `Como ${SUPPORT_ROLE_LABEL} no cambias precios.` : undefined}
                />
                <span className="muted">Si lo dejas vacío, esta categoría usa el costo general del torneo.</span>
              </label>
              <label>
                Cupo máximo
                <input type="number" min="1" placeholder="Opcional, ej. 16" value={divisionForm.max_participants} onChange={(e) => updateDivisionForm("max_participants", e.target.value)} />
                <span className="muted">Al llegar a este número de inscripciones aprobadas, el link público cerrará esta categoría (Cupo lleno).</span>
              </label>
              <label>
                Cierre de inscripciones (opcional)
                <input
                  type="date"
                  value={divisionForm.registration_deadline ?? ""}
                  min={registrationDeadline || undefined}
                  max={endDate || undefined}
                  onChange={(e) => updateDivisionForm("registration_deadline", e.target.value)}
                  aria-label="Cierre de inscripciones de la categoría"
                />
                <span className="muted">Vacío = usa la fecha general del torneo. No puede ser antes del cierre general ni después de la fecha final del torneo; si cae después del inicio, la categoría sigue abierta hasta ese día.</span>
              </label>
              <div className="form-actions tournament-division-form__actions">
                <button className="button button--primary" type="button" onClick={handleAddDivisionDraft} disabled={submitting}>
                  Agregar categoría
                </button>
              </div>
            </div>
            <div className="tournament-division-list">
              {divisionDrafts.length === 0 ? <p className="muted">Aún no hay categorías. Si no creas ninguna, el registro público seguirá funcionando como torneo único.</p> : null}
              {divisionDrafts.map((division, index) => (
                <article className="tournament-division-item" key={`${division.name}-${index}`}>
                  <div>
                    <strong>{division.name}</strong>
                    <p className="muted">{divisionDetailsLabel({
                      play_mode: division.play_mode ?? "singles",
                      format_config_json: division.team_play?.enabled ? { team_play: { enabled: true } } : null,
                      gender: division.gender ?? null,
                      age_min: division.age_min ?? null,
                      age_max: division.age_max ?? null,
                      level_label: division.level_label ?? null,
                      registration_cost: division.registration_cost ?? null,
                    })}</p>
                    {divisionBirthYearLabel(division, tournamentYear) ? (
                      <small className="muted">
                        Permitidos: {divisionBirthYearLabel(division, tournamentYear)} · Se evalúa por año calendario {tournamentYear}.
                      </small>
                    ) : null}
                  </div>
                  <button className="button button--ghost" type="button" onClick={() => removeDivisionDraft(index)} disabled={submitting}>
                    Quitar
                  </button>
                </article>
              ))}
            </div>
          </section>
        ) : token && !Number.isNaN(tournamentId) ? (
          <TournamentDivisionManager
            token={token}
            tournamentId={tournamentId}
            divisions={divisions}
            onDivisionsChange={setDivisions}
            tournamentStartDate={startDate}
            tournamentEndDate={endDate || null}
            tournamentRegistrationDeadline={registrationDeadline || null}
            compact
            onPendingChange={setPendingCategoryWarning}
            guardianFieldsAutoEnabled={guardianFieldsAutoEnabled}
          />
        ) : null}
        </div>

        {error && (
          <p role="alert" className="form-error setup-call-form__wide">
            {error}
          </p>
        )}
        <section className="setup-call-actions setup-data-actions wiz-bottombar">
          <div>
            <h2>{isCreate ? "Guardar datos" : "Guardar cambios"}</h2>
            <p className="muted">El siguiente paso usará estos campos para precargar la convocatoria.</p>
          </div>
          <div className="form__actions">
            <button className="btn wiz-btn-lime" type="submit" disabled={submitting || uploadingExtraSponsor !== null}>
              {submitting ? "Guardando…" : "Guardar y continuar"}
            </button>
            <Link className="btn btn--ghost" to={isCreate ? "/admin/tournaments" : `/admin/tournaments/${tournamentId}/setup/rules`}>
              Cancelar
            </Link>
          </div>
        </section>
      </form>
    </div>
  );
}